import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { API_ENDPOINTS } from '../utils/GlobalAPI';
import Navbar2 from '../Components/Navbar2';
import FooterSection from '../Components/Homepage2/FooterSection';
import PageSEO from '../Components/SEO/PageSEO';
import PersistentCTA from '../Components/CRO/PersistentCTA';
import StickyMobileCTA from '../Components/CRO/StickyMobileCTA';

const E = [0.16, 1, 0.3, 1];

const col = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: E } },
};

const UpSkill = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(API_ENDPOINTS.COURSES);
        if (!res.ok) throw new Error('Failed to fetch courses');
        const data = await res.json();
        setCourses(data);
      } catch (err) {
        setError(err.message || 'Error fetching courses');
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  return (
    <>
      <PageSEO 
        title="Best Upskill Development Agency In Ramamurthy Nagar"
        description="Looking to enhance your skills? Cyborgsapient Pvt. Ltd., a leading upskill development agency in Ramamurthy Nagar, provides expert-led courses for professional growth."
        keywords="upskill development agency in ramamurthy nagar, best upskill development agency in ramamurthy nagar, professional courses bangalore"
        url="https://cyborgsapient.com/upskill"
      />
      <Navbar2 />

      <div className="relative w-full bg-[#FAFBF9] text-[#1D1D1F] font-sans selection:bg-[#EAECE9] selection:text-[#1C2E1E] antialiased overflow-x-hidden pt-20 lg:pt-24">
        
        {/* Ambient glows */}
        <div className="absolute top-0 right-0 w-[600px] h-[500px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 80% 20%, rgba(112,0,255,0.04) 0%, transparent 60%)' }} />
        <div className="absolute top-[800px] left-0 w-[500px] h-[400px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 20% 80%, rgba(255,115,50,0.03) 0%, transparent 60%)' }} />

        {/* ── HERO HEADER SECTION ── */}
        <section className="relative overflow-hidden pt-16 sm:pt-24 lg:pt-28 pb-16 sm:pb-24 lg:pb-28">
          <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 text-left">
            <motion.div
              initial="hidden"
              animate="show"
              variants={col}
              className="max-w-3xl"
            >
              <motion.span variants={fadeUp} className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-8">
                ✦&nbsp;&nbsp;Skill Development&nbsp;&nbsp;✦
              </motion.span>
              <motion.h1 variants={fadeUp} className="text-[44px] sm:text-5xl md:text-6xl font-extralight text-[#1D1D1F]/30 leading-[1.05] tracking-tight">
                Transform your career.
              </motion.h1>
              <motion.h1 variants={fadeUp} className="text-[44px] sm:text-5xl md:text-6xl font-black text-[#1D1D1F] leading-[1.05] tracking-tight mb-8">
                <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2">
                  Upskill yourself.
                </span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-base sm:text-lg text-[#5A635A] font-light leading-[1.75] max-w-[48ch]">
                Explore our curated professional courses designed to accelerate your growth as creators, influencers, and builders. Master the dynamic digital ecosystem.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* ── COURSES SECTION ── */}
        <section className="relative bg-white border-t border-[#F1F3F1] py-24 sm:py-36 min-h-[50vh]">
          <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
            
            {error && (
              <div className="mb-8 p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-center font-medium max-w-md mx-auto">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="w-8 h-8 rounded-full border-2 border-[#1C2E1E]/20 border-t-[#1C2E1E] animate-spin" />
                <span className="text-[#5A635A] text-sm font-medium">Loading courses...</span>
              </div>
            ) : (
              <>
                {courses.length === 0 && !error ? (
                  <div className="text-center py-20 text-[#5A635A] font-light">
                    No courses available at the moment. Please check back later.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {courses.map((course, i) => (
                      <motion.div
                        key={course._id || i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.2), ease: 'easeOut' }}
                        className="bg-white border border-[#EAECE9] rounded-[28px] overflow-hidden flex flex-col justify-between group hover:border-[#1C2E1E] transition-all duration-350 shadow-sm hover:shadow-md min-h-[420px]"
                      >
                        
                        {/* Course Image */}
                        <div className="h-44 w-full overflow-hidden relative border-b border-[#F1F3F1]">
                          <img
                            src={course.img}
                            alt={course.title}
                            className="object-cover w-full h-full group-hover:scale-103 transition-transform duration-350"
                            loading="lazy"
                          />
                        </div>

                        {/* Card Content */}
                        <div className="p-6 flex flex-col flex-1 justify-between">
                          <div>
                            {/* Course Level Badge */}
                            <span className="inline-flex px-2.5 py-1 bg-[#F1F3F1] rounded-full text-[9px] font-bold uppercase tracking-wider text-[#5A635A] mb-3">
                              {course.level || 'Beginner'}
                            </span>
                            
                            {/* Title */}
                            <h3 className="text-lg font-bold text-black mb-2.5 leading-snug group-hover:bg-gradient-to-r group-hover:from-[#FF7332] group-hover:via-[#FF1E90] group-hover:to-[#7000FF] group-hover:bg-clip-text group-hover:text-transparent transition-all">
                              {course.title}
                            </h3>

                            {/* Description */}
                            <p className="text-[#5A635A] text-[13px] leading-relaxed mb-5 font-light line-clamp-3">
                              {course.desc}
                            </p>
                          </div>

                          <div>
                            {/* Metadata details */}
                            <div className="flex items-center gap-x-4 gap-y-2 flex-wrap text-[11px] text-[#5A635A]/70 mb-5 pt-3 border-t border-[#F1F3F1]">
                              <span className="inline-flex items-center gap-1.5 font-medium">
                                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" />
                                </svg>
                                {course.lessons} Lessons
                              </span>
                              <span className="inline-flex items-center gap-1.5 font-medium">
                                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
                                </svg>
                                {course.duration}
                              </span>
                            </div>

                            {/* Footer block: Price & CTA */}
                            <div className="flex items-center justify-between pt-3 border-t border-[#F1F3F1] mt-auto">
                              <span className="text-xl font-black bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent">
                                {course.price}
                              </span>
                              <button className="bg-[#1C2E1E] text-white hover:bg-black font-semibold px-5 py-2 rounded-full text-xs transition-colors duration-200">
                                Buy Now
                              </button>
                            </div>
                          </div>
                        </div>

                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}

          </div>
        </section>

        {/* ── BOTTOM CTA SECTION ── */}
        <section className="px-6 sm:px-10 lg:px-16 pb-28 sm:pb-36 bg-white max-w-[1200px] mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: E }}
            className="relative overflow-hidden rounded-[32px] bg-[#1C2E1E] px-10 sm:px-16 py-14 sm:py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-10"
          >
            {/* Ambient accent */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at 5% 50%, rgba(255,115,50,0.14) 0%, transparent 50%)' }}
            />

            <div className="relative z-10">
              <span className="text-[11px] uppercase tracking-[0.28em] text-white/40 font-semibold block mb-5">
                ✦&nbsp;&nbsp;Custom Solutions&nbsp;&nbsp;✦
              </span>
              <h3 className="text-3xl sm:text-4xl font-bold text-white tracking-tight leading-[1.05]">
                Want custom training<br />
                <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-1">
                  for your team?
                </span>
              </h3>
            </div>

            <div className="relative z-10 flex-shrink-0 flex flex-col sm:items-end gap-4">
              <a
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 bg-white text-[#1C2E1E] rounded-full text-sm font-semibold tracking-wide hover:bg-[#F4F5F4] transition-colors duration-200 group"
              >
                Get in touch
                <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3" className="group-hover:translate-x-1 transition-transform">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </a>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <FooterSection />

        <PersistentCTA />
        <StickyMobileCTA />
      </div>
    </>
  );
};

export default UpSkill;
